import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDownIcon, ChevronUpIcon, PauseIcon, PlayIcon } from 'lucide-react'
import Spinner from '@tandem/ui/Spinner'
import Table, { TableRegion } from '@tandem/ui/Table'
import formatDate from '@tandem/ui/util/formatDate'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $form from '@tandem/ui/form.css'
import $anim from '@tandem/ui/anim.css'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import $ from './-Sync.css'

export const Route = createFileRoute('/ribniy/')({ component: SyncPage })

let volume = (rows: string | number, bytes: number): number => Number(rows) * bytes
let fmtBytes = (v: number): string =>
  v >= 1e9 ? `${(v / 1e9).toFixed(1)} ГБ`
    : v >= 1e6 ? `${(v / 1e6).toFixed(0)} МБ`
      : v >= 1e3 ? `${(v / 1e3).toFixed(0)} КБ`
        : `${v} Б`
let fmtNum = (v: string | number | null): string => v === null ? '—' : Number(v).toLocaleString('ru')
let fmt = (d: Date | string | null | undefined): string => d ? formatDate(d) : '—'
let busy = (status?: string): boolean => status === 'pending' || status === 'running'

function SyncPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let qc = useQueryClient()
  let [sort, setSort] = useState<'entity' | 'size'>('size')
  let [picked, setPicked] = useState<Set<string>>(new Set())
  let [since, setSince] = useState('')
  let isAdmin = company?.role === 'admin'

  // Воркер в другом контейнере — прогресс тянем поллингом.
  let { data: status } = useQuery({
    ...trpc.ribniy.status.queryOptions(),
    refetchInterval: q => busy(q.state.data?.scan?.status) || q.state.data?.sync?.enabled ? 2000 : 10000,
  })
  let scan = status?.scan
  let sync = status?.sync
  let { data: rows = [], isFetching } = useQuery({
    ...trpc.ribniy.inventory.queryOptions(),
    refetchInterval: busy(scan?.status) || sync?.current ? 5000 : false,
  })

  let invalidate = () => {
    qc.invalidateQueries({ queryKey: trpc.ribniy.status.queryKey() })
    qc.invalidateQueries({ queryKey: trpc.ribniy.inventory.queryKey() })
  }
  let startScan = useMutation(trpc.ribniy.scan.mutationOptions({ onSuccess: invalidate }))
  let startSync = useMutation(trpc.ribniy.sync.mutationOptions({ onSuccess: invalidate }))
  let pause = useMutation(trpc.ribniy.pause.mutationOptions({ onSuccess: invalidate }))
  let resume = useMutation(trpc.ribniy.resume.mutationOptions({ onSuccess: invalidate }))

  // Таблицу надо перечитать в момент завершения скана и каждой сущности —
  // иначе после долгого сканирования экран пуст, а последняя сущность цикла
  // показывает устаревшее состояние.
  let changeKey = `${scan?.id}:${scan?.status}:${sync?.position}:${sync?.current}`
  let seen = useRef(changeKey)
  useEffect(() => {
    if (changeKey === seen.current) return
    seen.current = changeKey
    qc.invalidateQueries({ queryKey: trpc.ribniy.inventory.queryKey() })
  }, [changeKey, qc, trpc])

  // Поле показывает дату снимка, чекбоксы — настроенный состав, пока человек
  // их не поменял.
  let primed = useRef(false)
  useEffect(() => {
    if (primed.current || !rows.length || !status) return
    primed.current = true
    setSince(rows[0].since ? new Date(rows[0].since).toISOString().slice(0, 10) : '')
    setPicked(new Set(status.sync?.entities ?? []))
  }, [rows, status])

  let sorted = useMemo(() => {
    let list = [...rows]
    return sort === 'entity'
      ? list.sort((a, b) => a.entity.localeCompare(b.entity, 'ru'))
      : list.sort((a, b) => volume(b.rows, b.bytes_per_row) - volume(a.rows, a.bytes_per_row))
  }, [rows, sort])

  // Дата снимка — источник истины для переноса; поле сверху может от неё уйти,
  // и тогда показанная оценка больше не описывает то, что поедет.
  let snapshotSince = rows[0]?.since ? new Date(rows[0].since) : null
  let staleDate = sorted.length > 0 && since !== (snapshotSince ? snapshotSince.toISOString().slice(0, 10) : '')

  let total = sorted.reduce((s, r) => s + volume(r.rows, r.bytes_per_row), 0)
  let pickedTotal = sorted.filter(r => picked.has(r.entity)).reduce((s, r) => s + volume(r.rows, r.bytes_per_row), 0)
  let allPicked = sorted.length > 0 && picked.size === sorted.length
  let toggle = (entity: string) => setPicked(prev => {
    let next = new Set(prev)
    next.has(entity) ? next.delete(entity) : next.add(entity)
    return next
  })

  let syncedRows = new Map(rows.map(r => [r.entity, Number(r.synced_rows ?? 0)]))
  let phase = sync?.cycles ? 'Дельта' : 'Первичная выгрузка'
  let syncStatus = !sync?.entities.length ? 'Синхронизация не настроена.'
    : sync.current ? (
      <>
        <Spinner />
        {!sync.cycles && <progress class={$.bar} value={sync.position} max={sync.total} />}
        {phase}: {sync.position + 1} из {sync.total} · {sync.current}
        {(syncedRows.get(sync.current) ?? 0) > 0 && ` · ${fmtNum(syncedRows.get(sync.current)!)} строк`}
      </>
    )
    : !sync.enabled ? `На паузе${sync.cycles ? `, данные на ${fmt(sync.cycle_finished_at)}` : sync.position ? `: ${sync.position} из ${sync.total}` : ''}.`
    : sync.position > 0 || !sync.next_at ? <><Spinner /> {phase}: {sync.position ? `${sync.position} из ${sync.total}` : 'готовится'}</>
    : `Данные на ${fmt(sync.cycle_finished_at)}, следующая дельта ${fmt(sync.next_at)}.`

  return (
    <div class={$.page}>
      <div class={clsx($layout.container, $layout.center, $.controls)}>
        <h3>Синхронизация 1С</h3>

        <div class={clsx($form.row, $.controlRow)}>
          <label class={$.date}>
            Данные с
            <input type='date' value={since} onChange={e => setSince(e.currentTarget.value)} disabled={!isAdmin} />
          </label>
          <button class='secondary' disabled={!isAdmin || busy(scan?.status)} onClick={() => startScan.mutate({ since: since || null })}>
            {sorted.length ? 'Пересканировать' : 'Сканировать'}
          </button>
          <button
            disabled={!isAdmin || !picked.size || staleDate || busy(scan?.status)}
            title={staleDate ? 'Дата изменилась — пересканируйте, чтобы оценка ей соответствовала' : undefined}
            onClick={() => startSync.mutate({ entities: [...picked] })}
          >
            Синхронизировать
          </button>
          {!!sync?.entities.length && (
            <button class='secondary' disabled={!isAdmin} onClick={() => (sync!.enabled ? pause : resume).mutate()}>
              {sync.enabled ? <><PauseIcon size={14} /> Пауза</> : <><PlayIcon size={14} /> Продолжить</>}
            </button>
          )}
        </div>

        <p class={clsx($type.small, $type.muted, $.hint)}>
          {busy(scan?.status) ? (
            <>
              <Spinner />
              {scan!.progress.total
                ? `Сканирование: ${scan!.progress.done ?? 0} из ${scan!.progress.total} · ${scan!.progress.current}`
                : 'Сканирование: читает метаданные'}
            </>
          ) : scan?.status === 'failed' ? <span class={$type.error}>Ошибка сканирования: {scan.error}</span>
            : sorted.length ? (
              <>
                Оценка {snapshotSince ? `с ${snapshotSince.toLocaleDateString('ru')}` : 'за всё время'}:
                {' '}сущностей {sorted.length}, около {fmtBytes(total)}.
                {staleDate && ' Дата изменилась — пересканируйте, чтобы оценка ей соответствовала.'}
              </>
            ) : 'Сканирование считает строки и объём каждой сущности с указанной даты, ничего не выгружая. Дата отсекает документы и регистры, справочники переносятся целиком.'}
          {!isAdmin && ' Запуск доступен администратору.'}
        </p>
      </div>

      <TableRegion>
        {sorted.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th class={$.pick}>
                  <input
                    type='checkbox'
                    checked={allPicked}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(sorted.map(r => r.entity)))}
                  />
                </th>
                <th class={$.sortable} onClick={() => setSort('entity')}>
                  Сущность<span class={$.arrow}>{sort === 'entity' && <ChevronUpIcon size={13} />}</span>
                </th>
                <th class={clsx($.sortable, $.num)} onClick={() => setSort('size')}
                    title='Точное число строк × средний размер строки по выборке; у табличных частей — доля от родителя'>
                  Объём ≈<span class={$.arrow}>{sort === 'size' && <ChevronDownIcon size={13} />}</span>
                </th>
                <th class={$.num}>Строк</th>
                <th title='Строк записано в последнем цикле; в дельте 0 — изменений не было'>Изменено за цикл</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.entity} class={clsx(r.entity === sync?.current && $anim.pulse)}>
                  <td class={$.pick}>
                    <input type='checkbox' checked={picked.has(r.entity)} onChange={() => toggle(r.entity)} />
                  </td>
                  <td>{r.entity}</td>
                  <td class={$.num} title={r.estimated ? 'Оценка по доле от родительского документа' : undefined}>
                    {r.estimated && <span class={$type.muted}>≈ </span>}
                    {fmtBytes(volume(r.rows, r.bytes_per_row))}
                  </td>
                  <td class={clsx($.num, $type.muted)}>{fmtNum(r.rows)}</td>
                  <td class={r.error ? $type.error : $type.muted} title={r.error ?? undefined}>
                    {Number(r.synced_rows) ? fmtNum(r.synced_rows) : '—'}
                    {r.watermark && ` · до ${new Date(r.watermark).toLocaleDateString('ru')}`}
                    {r.error && ' · ошибка'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} class={$.footerTd}>
                  <div class={clsx($.footer, $type.small, $type.muted)}>
                    <div class={$.status}>{syncStatus}</div>
                    <div class={$.count}>
                      Выбрано: {picked.size} из {sorted.length}{picked.size > 0 && ` · около ${fmtBytes(pickedTotal)}`}
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        ) : (
          <div class={$.emptyState}>
            {isFetching || busy(scan?.status) ? <Spinner /> : <strong>Данных пока нет — запустите сканирование.</strong>}
          </div>
        )}
      </TableRegion>
    </div>
  )
}
